import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  forwardRef,
} from 'react';
import classNames from 'classnames';

import Icons from 'assets/icons';

export interface SearchableSelectItem {
  [key: string]: unknown;
}

interface RenderItemParams<TItem> {
  key: unknown;
  item: TItem;
  label: string;
  value: unknown;
  isSelected: boolean;
  onSelect: () => void;
}

export interface SearchableSelectContext<
  TItem extends SearchableSelectItem = SearchableSelectItem
> {
  isOpen: boolean;
  setIsOpen: (value: boolean | ((prev: boolean) => boolean)) => void;
  search: string;
  setSearch: (value: string) => void;
  items: TItem[];
  filteredItems: TItem[];
  selectedItem: TItem | null;
  selectedLabel: string;
  selectedValue: unknown;
  isLoading: boolean;
  disabled: boolean;
  handleToggle: () => void;
  handleSelect: (item: TItem) => void;
  handleClear: (e?: React.MouseEvent) => void;
  getLabel: (item: TItem | null) => string;
  getValue: (item: TItem | null) => unknown;
  searchInputRef: React.RefObject<HTMLInputElement | null>;
  panelContent: React.JSX.Element;
  triggerProps?: Record<string, unknown>;
}

export interface SearchableSelectProps<
  TItem extends SearchableSelectItem = SearchableSelectItem
> {
  items?: TItem[];
  selectedItem?: TItem | null;
  onSelect?: (item: TItem | null) => void;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  isLoading?: boolean;
  disabled?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  notFoundMessage?: string;
  labelKey?: string | ((item: TItem) => string);
  valueKey?: string | ((item: TItem) => unknown);
  renderTrigger?: (
    context: SearchableSelectContext<TItem>
  ) => React.JSX.Element;
  renderItem?: (params: RenderItemParams<TItem>) => React.JSX.Element;
  renderPanel?: (context: SearchableSelectContext<TItem>) => React.JSX.Element;
  panelClassName?: string;
  searchInputClassName?: string;
  itemsContainerClassName?: string;
  autoFocusSearch?: boolean;
  closeOnSelect?: boolean;
}

const SearchableSelectImpl = forwardRef<
  HTMLDivElement,
  SearchableSelectProps<SearchableSelectItem>
>(
  (
    {
      items = [],
      selectedItem,
      onSelect,
      isOpen: controlledIsOpen,
      onOpenChange,
      isLoading = false,
      disabled = false,
      searchPlaceholder = 'Search...',
      emptyMessage = 'No items available',
      notFoundMessage = 'No items found',
      labelKey = 'name',
      valueKey = 'id',
      renderTrigger,
      renderItem,
      renderPanel,
      panelClassName = '',
      searchInputClassName = '',
      itemsContainerClassName = '',
      autoFocusSearch = true,
      closeOnSelect = true,
    },
    _ref
  ) => {
    const [internalIsOpen, setInternalIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement | null>(null);
    const searchInputRef = useRef<HTMLInputElement | null>(null);

    const isControlled = controlledIsOpen !== undefined;
    const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

    const setIsOpen = useCallback(
      (value: boolean | ((prev: boolean) => boolean)) => {
        const newValue = typeof value === 'function' ? value(isOpen) : value;

        if (isControlled) {
          onOpenChange?.(newValue);
        } else {
          setInternalIsOpen(newValue);
        }
      },
      [isControlled, isOpen, onOpenChange]
    );

    const getLabel = useCallback(
      (item: SearchableSelectItem | null) => {
        if (!item) return '';
        if (typeof labelKey === 'function') return labelKey(item);

        return (item[labelKey] as string) || '';
      },
      [labelKey]
    );

    const getValue = useCallback(
      (item: SearchableSelectItem | null) => {
        if (!item) return undefined;
        if (typeof valueKey === 'function') return valueKey(item);

        return item[valueKey];
      },
      [valueKey]
    );

    const filteredItems = useMemo(() => {
      if (!search.trim()) return items;

      const searchLower = search.toLowerCase();

      return items.filter((item) =>
        getLabel(item)?.toLowerCase().includes(searchLower)
      );
    }, [items, search, getLabel]);

    useEffect(() => {
      if (isOpen && autoFocusSearch && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [isOpen, autoFocusSearch]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
          setSearch('');
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen, setIsOpen]);

    const handleToggle = useCallback(() => {
      if (disabled) return;

      setIsOpen((prev: boolean) => !prev);

      if (isOpen) {
        setSearch('');
      }
    }, [disabled, isOpen, setIsOpen]);

    const handleSelect = useCallback(
      (item: SearchableSelectItem) => {
        onSelect?.(item);

        if (closeOnSelect) {
          setIsOpen(false);
          setSearch('');
        }
      },
      [onSelect, closeOnSelect, setIsOpen]
    );

    const handleClear = useCallback(
      (e?: React.MouseEvent) => {
        e?.stopPropagation();
        onSelect?.(null);
      },
      [onSelect]
    );

    const selectedLabel = getLabel(selectedItem ?? null);
    const selectedValue = getValue(selectedItem ?? null);

    const defaultSearchInput = (
      <div className="relative text-gray-500">
        <Icons.Search customClass="absolute left-0 top-1/2 -translate-y-1/2 size-4" />
        <input
          ref={searchInputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={searchPlaceholder}
          className={classNames(
            'w-full pl-6 text-base focus:outline-none bg-transparent',
            searchInputClassName
          )}
        />
      </div>
    );

    const defaultItemsList = (
      <div className={classNames('space-y-0.5 py-2', itemsContainerClassName)}>
        {filteredItems.map((item) => {
          const itemValue = getValue(item);
          const itemLabel = getLabel(item);
          const isSelected = selectedValue === itemValue;

          if (renderItem) {
            return renderItem({
              key: itemValue,
              item,
              label: itemLabel,
              value: itemValue,
              isSelected,
              onSelect: () => handleSelect(item),
            });
          }

          return (
            <button
              key={itemValue as React.Key}
              onClick={() => handleSelect(item)}
              title={itemLabel}
              className={classNames(
                'w-full text-left px-3 py-2 text-base font-semibold rounded-lg transition-colors truncate',
                {
                  'bg-blue-50 text-blue-700': isSelected,
                  'text-gray-700 hover:bg-navy-50': !isSelected,
                }
              )}
            >
              {itemLabel}
            </button>
          );
        })}
      </div>
    );

    const panelContent = (
      <>
        <div className="px-4 py-2.5 border-b border-navy-200">
          {defaultSearchInput}
        </div>

        <div className="overflow-y-auto flex-1 px-2 custom-scroll">
          {isLoading ? (
            <div className="px-3 py-4 text-sm text-gray-600 text-center">
              Loading...
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="px-3 py-4 text-sm text-gray-600 text-center">
              {search ? notFoundMessage : emptyMessage}
            </div>
          ) : (
            defaultItemsList
          )}
        </div>
      </>
    );

    const contextValue: SearchableSelectContext = {
      isOpen,
      setIsOpen,
      search,
      setSearch,
      items,
      filteredItems,
      selectedItem: selectedItem ?? null,
      selectedLabel,
      selectedValue,
      isLoading,
      disabled,
      handleToggle,
      handleSelect,
      handleClear,
      getLabel,
      getValue,
      searchInputRef,
      panelContent,
    };

    return (
      <div ref={containerRef}>
        {renderTrigger?.({
          ...contextValue,
          triggerProps: {
            onClick: handleToggle,
            disabled,
            'aria-expanded': isOpen,
            'aria-haspopup': 'listbox',
          },
        })}

        {isOpen &&
          (renderPanel ? (
            renderPanel({
              ...contextValue,
              panelContent,
            })
          ) : (
            <div
              className={classNames(
                'absolute mt-1 bg-white rounded-xl border border-navy-200 w-full max-h-[300px] flex flex-col z-50',
                panelClassName
              )}
              role="listbox"
            >
              {panelContent}
            </div>
          ))}
      </div>
    );
  }
);

SearchableSelectImpl.displayName = 'SearchableSelect';

const SearchableSelect = SearchableSelectImpl as unknown as <
  TItem extends SearchableSelectItem = SearchableSelectItem
>(
  props: SearchableSelectProps<TItem> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement;

export { SearchableSelect };
export default SearchableSelect;
